import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Card, CardBody, Col, Button, Spinner, Row, Input } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios from "axios";
import useCurrency, { baseUrl } from "../../../Utils/FileUpload";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaFilePdf, FaFileExcel } from "react-icons/fa";
import { exportToPDF } from "../../../Utils/pdfExportUtils";
import { excelExportUtils } from "../../../Utils/excelExportUtils";
import { useAuth } from "../../../Context/AuthContext";

interface Agent {
  id: string;
  agent_name: string;
  phone: string | number;
  total_amount: string;
  total_call_duration: string;
  total_call_duration_seconds: number;
  currency: string;
  total_unique_customers: number;
  total_calls: string;
  total_calls_raw: number;
  email: string;
  total_amount_without_currency: number;
  isFooter?: boolean;
}

const customStyles = {
  headCells: {
    style: {
      fontSize: "16px",
      fontWeight: "600",
      color: "#308e87",
      backgroundColor: "#f8f9fa",
      borderBottom: "2px solid #308e87",
    },
  },
  rows: {
    style: {
      borderBottom: "1px solid #dee2e6",
    },
  },
  table: {
    style: {
      border: "1px solid #dee2e6",
      borderRadius: "8px",
    },
  },
};

const conditionalRowStyles = [
  {
    when: (row: any) => row.isFooter === true,
    style: {
      fontWeight: "800",
      backgroundColor: "#f8f9fa",
      color: "#308e87",
      borderTop: "2px solid #dee2e6",
      fontSize: "16px",
    },
  },
];

export default function AgentReport() {
  const token = localStorage.getItem("token");
  const { currency } = useCurrency();
  const { hasPermission } = useAuth();

  // State Management
  const [filterText, setFilterText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filteredItems, setFilteredItems] = useState<Agent[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [grandTotals, setGrandTotals] = useState({
    totalAmount: 0,
    totalDurationSeconds: 0,
    totalCustomers: 0,
    totalCalls: 0,
  });

  // Debounce search input (350ms) — prevents focus loss and excess API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filterText);
    }, 350);
    return () => clearTimeout(timer);
  }, [filterText]);

  // Main Data Fetcher
  const fetchData = useCallback(
    async (page = currentPage, currentPerPage = perPage) => {
      setTableLoading(true);
      try {
        const response = await axios.get(`${baseUrl}agent/get_all_report`, {
          params: {
            page,
            per_page: currentPerPage,
            search: debouncedSearch.trim() !== "" ? debouncedSearch : undefined,
            sort_field: sortField,
            sort_direction: sortDir,
          },
          headers: { Authorization: `Bearer ${token}` },
        });
        setFilteredItems(response.data.data || []);
        setTotalRows(response.data.total || 0);
        setCurrentPage(response.data.current_page || 1);
        if (response.data.grand_totals) {
          const gt = response.data.grand_totals;
          setGrandTotals({
            totalAmount: gt.total_amount || 0,
            totalDurationSeconds: gt.total_duration_seconds || 0,
            totalCustomers: gt.total_customers || 0,
            totalCalls: gt.total_calls || 0,
          });
        }
      } catch (error) {
        console.error("Error fetching agent report data:", error);
      } finally {
        setTableLoading(false);
      }
    },
    [currentPage, perPage, debouncedSearch, sortField, sortDir, token]
  );

  // Sort Handler
  const handleSort = useCallback(
    (column: any, direction: "asc" | "desc") => {
      setSortField(column.sortField || "created_at");
      setSortDir(direction);
      setCurrentPage(1);
    },
    []
  );

  // Auto-Fetch on change
  useEffect(() => {
    fetchData(currentPage, perPage);
  }, [currentPage, perPage, debouncedSearch, sortField, sortDir]);

  // Pagination Handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  };

  // Unpaginated Fetcher for PDF / Excel Export (respects current sort + search)
  const fetchAllDataForExport = async (): Promise<Agent[]> => {
    try {
      const response = await axios.get(`${baseUrl}agent/get_all_no_pagination`, {
        params: {
          search: debouncedSearch.trim() !== "" ? debouncedSearch : undefined,
          sort_field: sortField,
          sort_direction: sortDir,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data.data || [];
    } catch (error) {
      console.error("Error fetching all agent data for export:", error);
      return [];
    }
  };

  // Compute formatted grand duration from API grand totals
  const grandTotalsFormatted = useMemo(() => {
    const s = grandTotals.totalDurationSeconds;
    const hours = Math.floor(s / 3600);
    const minutes = Math.floor((s % 3600) / 60);
    const seconds = s % 60;
    let dur = "";
    if (hours > 0) dur += `${hours}h `;
    if (minutes > 0) dur += `${minutes}m `;
    if (seconds > 0 || (hours === 0 && minutes === 0)) dur += `${seconds}s`;
    return dur.trim();
  }, [grandTotals.totalDurationSeconds]);

  // Table rows: footer appears ONLY on the last page, but shows grand totals of ALL pages
  const columnsData = useMemo<Agent[]>(() => {
    if (filteredItems.length === 0) return [];

    const totalPages = Math.ceil(totalRows / perPage) || 1;
    const isLastPage = currentPage >= totalPages;

    if (!isLastPage) return filteredItems;

    return [
      ...filteredItems,
      {
        isFooter: true,
        id: "footer",
        agent_name: "Total",
        phone: "",
        email: "",
        currency: "",
        total_call_duration: grandTotalsFormatted,
        total_call_duration_seconds: grandTotals.totalDurationSeconds,
        total_amount: String(grandTotals.totalAmount),
        total_amount_without_currency: grandTotals.totalAmount,
        total_unique_customers: grandTotals.totalCustomers,
        total_calls: `${grandTotals.totalCalls}`,
        total_calls_raw: grandTotals.totalCalls,
      },
    ];
  }, [filteredItems, grandTotals, grandTotalsFormatted, currentPage, perPage, totalRows]);

  // Memoized Columns
  const columns = useMemo(() => {
    const currencyHeader = currency ? ` (${currency})` : "";
    return [
      {
        name: <div>Sl.No</div>,
        cell: (row: Agent, index: number) => {
          if (row.isFooter) return "";
          return (currentPage - 1) * perPage + index + 1;
        },
        width: "75px",
      },
      {
        name: <div>Agent Name</div>,
        sortable: true,
        sortField: "agent_name",
        cell: (row: Agent) => {
          if (row.isFooter) return <strong>Total</strong>;
          return row.agent_name || "N/A";
        },
      },
      {
        name: <div>Contact Number</div>,
        sortable: true,
        sortField: "phone",
        cell: (row: Agent) => {
          if (row.isFooter) return "";
          return row.phone || "N/A";
        },
      },
      {
        name: <div>Total Call Duration</div>,
        sortable: true,
        sortField: "total_call_duration",
        cell: (row: Agent) => row.total_call_duration || "0s",
      },
      {
        name: <div>Total Customers (Calls)</div>,
        sortable: true,
        sortField: "total_unique_customers",
        cell: (row: Agent) => {
          if (row.isFooter)
            return `${grandTotals.totalCustomers} (${grandTotals.totalCalls})`;
          return `${row.total_unique_customers} (${row.total_calls_raw})`;
        },
      },
      {
        name: <div>Total Amount{currencyHeader}</div>,
        sortable: true,
        sortField: "total_amount",
        cell: (row: Agent) => {
          if (row.isFooter) return grandTotals.totalAmount;
          const val = row.total_amount_without_currency;
          if (val === undefined || val === null) return "N/A";
          return String(val).replace(/[^0-9.]/g, "") || "N/A";
        },
      },
    ];
  }, [currency, currentPage, perPage, grandTotals]);

  // PDF Export
  const exportPDF = async () => {
    const allData = await fetchAllDataForExport();
    if (!allData.length) {
      toast.warn("No agent data available for export.", {
        position: "top-right",
        autoClose: 1000,
      });
      return;
    }

    const currencyHeader = currency ? ` (${currency})` : "";
    const currencyMap: Record<string, string> = {
      "₹": "Rs.",
      $: "USD",
      "€": "EUR",
      "£": "GBP",
    };
    const safeCurrency = currencyMap[currency] ?? currency;

    let totalSeconds = 0;
    let totalAmount = 0;
    let totalCustomers = 0;
    let totalCalls = 0;

    const rows = allData.map((row, idx) => {
      totalSeconds += Number(row.total_call_duration_seconds) || 0;
      totalAmount += Number(row.total_amount_without_currency) || 0;
      totalCustomers += Number(row.total_unique_customers) || 0;
      totalCalls += Number(row.total_calls_raw) || 0;

      return [
        idx + 1,
        row.agent_name || "N/A",
        row.phone || "N/A",
        row.total_call_duration || "0s",
        `${row.total_unique_customers || 0} (${row.total_calls_raw || 0})`,
        String(row.total_amount_without_currency ?? 0),
      ];
    });

    // Footer totals
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    let dur = "";
    if (hours > 0) dur += `${hours}h `;
    if (mins > 0) dur += `${mins}m `;
    if (secs > 0 || (!hours && !mins)) dur += `${secs}s`;
    rows.push(["Total", "", "", dur.trim(), `${totalCustomers} (${totalCalls})`, totalAmount.toFixed(2)]);

    exportToPDF({
      fileName: "Agent_Report",
      columns: [
        "Sl.No",
        "Agent Name",
        "Contact Number",
        "Total Call Duration",
        "Total Customers (Calls)",
        `Total Amount${safeCurrency ? ` (${safeCurrency})` : ""}`,
      ],
      data: rows,
      emptyDataMessage: "No agent data available for export.",
    });
  };

  // Excel Export
  const exportExcel = async () => {
    const allData = await fetchAllDataForExport();
    if (!allData.length) {
      toast.warn("No agent data available for export.", {
        position: "top-right",
        autoClose: 1000,
      });
      return;
    }

    const currencyHeader = currency ? ` (${currency})` : "";

    let totalSeconds = 0;
    let totalAmount = 0;
    let totalCustomers = 0;
    let totalCalls = 0;

    const tableData = allData.map((row, idx) => {
      totalSeconds += Number(row.total_call_duration_seconds) || 0;
      totalAmount += Number(row.total_amount_without_currency) || 0;
      totalCustomers += Number(row.total_unique_customers) || 0;
      totalCalls += Number(row.total_calls_raw) || 0;

      return {
        "Sl.No": idx + 1,
        "Agent Name": row.agent_name || "N/A",
        "Contact Number": row.phone || "N/A",
        "Total Call Duration": row.total_call_duration || "0s",
        "Total Customers (Calls)": `${row.total_unique_customers || 0} (${row.total_calls_raw || 0})`,
        [`Total Amount${currencyHeader}`]: row.total_amount_without_currency ?? 0,
      };
    });

    // Footer row
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    let dur = "";
    if (hours > 0) dur += `${hours}h `;
    if (mins > 0) dur += `${mins}m `;
    if (secs > 0 || (!hours && !mins)) dur += `${secs}s`;
    tableData.push({
      "Sl.No": "" as any,
      "Agent Name": "Total",
      "Contact Number": "",
      "Total Call Duration": dur.trim(),
      "Total Customers (Calls)": `${totalCustomers} (${totalCalls})`,
      [`Total Amount${currencyHeader}`]: Number(totalAmount.toFixed(2)),
    });

    excelExportUtils(tableData, "Agent_Report", "Agents Summary");
  };

  return (
    <>
      <div className="page-body">
        <Breadcrumbs
          pageTitle="Agent Report"
          parent="Agent"
          title="Agent Report"
        />
        <Col xl={12} className="mb-lg-3">
          <Card>
            <CardBody>
              <Row className="justify-content-between mb-2">
                {/* Left: PDF & Excel buttons */}
                <Col xl={2}>
                  <div className="d-flex gap-2">
                    {hasPermission("agent-report.download_pdf") && (
                      <Button
                        color="primary"
                        onClick={exportPDF}
                        className="d-flex justify-content-center align-items-center p-0"
                        style={{ width: "30px", height: "30px" }}
                        title="Export PDF"
                      >
                        <FaFilePdf size={16} />
                      </Button>
                    )}
                    {hasPermission("agent-report.download_excel") && (
                      <Button
                        color="secondary"
                        onClick={exportExcel}
                        className="d-flex justify-content-center align-items-center p-0"
                        style={{ width: "30px", height: "30px" }}
                        title="Export Excel"
                      >
                        <FaFileExcel size={16} />
                      </Button>
                    )}
                  </div>
                </Col>

                {/* Right: Search box */}
                <Col xl={3}>
                  <div className="d-flex align-items-center">
                    <Input
                      type="text"
                      placeholder="Search..."
                      value={filterText}
                      onChange={(e) => {
                        setFilterText(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </div>
                </Col>
              </Row>

              <div className="table-responsive custom-scrollbar mt-3">
                <DataTable
                  data={columnsData}
                  columns={columns}
                  highlightOnHover
                  striped
                  customStyles={customStyles}
                  pagination
                  paginationServer
                  paginationPerPage={perPage}
                  paginationTotalRows={totalRows}
                  onChangePage={handlePageChange}
                  onChangeRowsPerPage={handlePerRowsChange}
                  paginationDefaultPage={currentPage}
                  responsive
                  sortServer
                  onSort={handleSort}
                  conditionalRowStyles={conditionalRowStyles}
                  progressPending={tableLoading}
                  progressComponent={
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ minHeight: "300px", width: "100%" }}
                    >
                      <Spinner color="primary" />
                    </div>
                  }
                />
              </div>
            </CardBody>
          </Card>
        </Col>
        <ToastContainer />
      </div>
    </>
  );
}
